class RelatedProductsSlider extends HTMLElement {
  constructor() {
    super();
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragScrollStart = 0;
    this.activePointerId = null;
    this.initialized = false;
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.destroy();
  }

  init() {
    this.track = this.querySelector('.related-products-slider__track');
    this.prevButton = this.querySelector('[name="previous"]');
    this.nextButton = this.querySelector('[name="next"]');

    if (!this.track) return;

    if (this.initialized) {
      this.updateButtons();
      return;
    }

    this.initialized = true;
    this.cards = this.track.querySelectorAll('.related-products__card');

    this.onScroll = this.updateButtons.bind(this);
    this.onResize = this.updateButtons.bind(this);
    this.onPrevClick = () => this.scrollByItem(-1);
    this.onNextClick = () => this.scrollByItem(1);

    this.boundPointerDown = this.onPointerDown.bind(this);
    this.boundPointerMove = this.onPointerMove.bind(this);
    this.boundPointerUp = this.onPointerUp.bind(this);
    this.boundDragStart = this.onDragStart.bind(this);

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);
    this.track.addEventListener('pointerdown', this.boundPointerDown);
    this.track.addEventListener('dragstart', this.boundDragStart);

    this.prevButton?.addEventListener('click', this.onPrevClick);
    this.nextButton?.addEventListener('click', this.onNextClick);

    this.track.querySelectorAll('img').forEach((image) => {
      image.setAttribute('draggable', 'false');
      image.addEventListener('dragstart', this.boundDragStart);
    });

    requestAnimationFrame(() => this.updateButtons());
  }

  destroy() {
    this.endDrag(false);

    this.track?.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    this.track?.removeEventListener('pointerdown', this.boundPointerDown);
    this.track?.removeEventListener('dragstart', this.boundDragStart);
    this.prevButton?.removeEventListener('click', this.onPrevClick);
    this.nextButton?.removeEventListener('click', this.onNextClick);

    this.track?.querySelectorAll('img').forEach((image) => {
      image.removeEventListener('dragstart', this.boundDragStart);
    });

    this.initialized = false;
  }

  onDragStart(event) {
    event.preventDefault();
  }

  onPointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    this.activePointerId = event.pointerId;
    this.isDragging = false;
    this.dragStartX = event.clientX;
    this.dragScrollStart = this.track.scrollLeft;

    window.addEventListener('pointermove', this.boundPointerMove);
    window.addEventListener('pointerup', this.boundPointerUp);
    window.addEventListener('pointercancel', this.boundPointerUp);
  }

  onPointerMove(event) {
    if (this.activePointerId === null || event.pointerId !== this.activePointerId) return;

    const deltaX = event.clientX - this.dragStartX;

    if (!this.isDragging) {
      if (Math.abs(deltaX) < 5) return;

      this.isDragging = true;
      this.track.classList.add('is-dragging');
    }

    this.track.scrollLeft = this.dragScrollStart - deltaX;
    event.preventDefault();
  }

  onPointerUp(event) {
    if (this.activePointerId === null || event.pointerId !== this.activePointerId) return;

    this.endDrag(true);
  }

  endDrag(shouldSnap) {
    const wasDragging = this.isDragging;

    this.isDragging = false;
    this.activePointerId = null;
    this.track?.classList.remove('is-dragging');

    window.removeEventListener('pointermove', this.boundPointerMove);
    window.removeEventListener('pointerup', this.boundPointerUp);
    window.removeEventListener('pointercancel', this.boundPointerUp);

    if (shouldSnap && wasDragging) {
      this.snapToNearestCard();
    }

    this.updateButtons();
  }

  snapToNearestCard() {
    const cards = this.track?.querySelectorAll('.related-products__card');
    if (!cards?.length) return;

    const scrollLeft = this.track.scrollLeft;
    let nearestCard = cards[0];
    let nearestDistance = Infinity;

    cards.forEach((card) => {
      const distance = Math.abs(card.offsetLeft - scrollLeft);

      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestCard = card;
      }
    });

    this.track.scrollTo({
      left: nearestCard.offsetLeft,
      behavior: 'smooth',
    });
  }

  scrollByItem(direction) {
    const cards = this.track?.querySelectorAll('.related-products__card');
    const card = cards?.[0];
    if (!card || !this.track) return;

    const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 0;
    const step = card.offsetWidth + gap;

    this.track.scrollBy({
      left: direction * step,
      behavior: 'smooth',
    });
  }

  updateButtons() {
    if (!this.track) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const scrollLeft = this.track.scrollLeft;
    const canScroll = maxScroll > 1;

    if (this.prevButton) {
      if (!canScroll || scrollLeft <= 1) {
        this.prevButton.setAttribute('disabled', 'disabled');
      } else {
        this.prevButton.removeAttribute('disabled');
      }
    }

    if (this.nextButton) {
      if (!canScroll || scrollLeft >= maxScroll - 1) {
        this.nextButton.setAttribute('disabled', 'disabled');
      } else {
        this.nextButton.removeAttribute('disabled');
      }
    }
  }
}

if (!customElements.get('related-products-slider')) {
  customElements.define('related-products-slider', RelatedProductsSlider);
}
